import { useId } from 'react';
/** Side of the square pattern drawn around the icon; its centre sits on the icon's centre. */
const SIZE = 320;
const CENTER = SIZE / 2;
const GRID = 24;

/** Fades a centred pattern out towards its edges. */
const RADIAL_FADE = {
  maskImage: 'radial-gradient(circle at center, black 0%, black 18%, transparent 62%)',
  WebkitMaskImage: 'radial-gradient(circle at center, black 0%, black 18%, transparent 62%)'
};

/** Fades the full-width grid out towards the title. */
const DOWNWARD_FADE = {
  maskImage: 'linear-gradient(to bottom, black 0%, black 25%, transparent 100%)',
  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 25%, transparent 100%)'
};

/**
 * Decorative linework behind the confirm dialog's icon. Lines use the border token at 60% so they stay faint in both themes;
 * only the innermost circle wave picks up the dialog's tone (`--confirm-tone`).
 *
 * `iconCenter` is the icon's centre in the paper's coordinates, so centred patterns stay concentric with it.
 */
export function ConfirmDialogPattern({
  variant,
  iconCenter
}) {
  // useId's characters aren't safe inside url(#…); keep letters and digits.
  const id = `confirm-pattern-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  if (variant === 'none') return null;
  if (variant === 'grid-extended') {
    return <svg aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-40 w-full text-border opacity-60" style={DOWNWARD_FADE} data-testid="confirm-dialog-pattern">
                <defs>
                    <pattern id={`${id}-grid`} width={GRID} height={GRID} patternUnits="userSpaceOnUse" x={iconCenter.x % GRID} y={iconCenter.y % GRID}>
                        <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="currentColor" strokeWidth={1} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill={`url(#${id}-grid)`} />
            </svg>;
  }
  return <svg aria-hidden viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} className="pointer-events-none absolute text-border opacity-60" style={{
    left: iconCenter.x - CENTER,
    top: iconCenter.y - CENTER,
    ...RADIAL_FADE
  }} data-testid="confirm-dialog-pattern">
            {variant === 'circle-waves' && [36, 56, 78, 102, 128].map((r, i) => <circle key={r} cx={CENTER} cy={CENTER} r={r} fill="none" stroke={i === 0 ? 'color-mix(in srgb, var(--confirm-tone) 14%, transparent)' : 'currentColor'} strokeWidth={1} />)}
            {variant === 'square-waves' && [72, 112, 152, 196, 240].map((side, i) => <rect key={side} x={CENTER - side / 2} y={CENTER - side / 2} width={side} height={side} rx={side * 0.16} fill="none" stroke="currentColor" strokeWidth={1} transform={`rotate(${(i + 1) * 5} ${CENTER} ${CENTER})`} />)}
            {variant === 'grid-focused' && <>
                    <defs>
                        <pattern id={`${id}-grid`} width={GRID} height={GRID} patternUnits="userSpaceOnUse" x={CENTER % GRID} y={CENTER % GRID}>
                            <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="currentColor" strokeWidth={1} />
                        </pattern>
                    </defs>
                    <rect width={SIZE} height={SIZE} fill={`url(#${id}-grid)`} />
                </>}
        </svg>;
}